import { Component, inject, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { CentrexSdaModel } from 'src/app/models/centrex-sda.model';
import { HistoriqueComponent } from '../../historique/historique.component';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CentrexSdasService } from 'src/app/services/centrex-sdas.service';
import { CentrexExtensionsService } from 'src/app/services/centrex-extensions.service';
import { MatSelectModule } from '@angular/material/select';
import { CentrexExtensionModel } from 'src/app/models/centrex-extension.model';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-sdas-form',
  standalone: true,
  templateUrl: './sdas-form.component.html',
  styleUrl: './sdas-form.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatButtonModule,
    MatSelectModule,
    HistoriqueComponent,
    MatAutocompleteModule,
    NgxSpinnerModule,
  ],
})
export class SdasFormComponent implements OnInit {
  objectId!: string;
  uidEntreprise!: string;
  platform!: string;
  form!: FormGroup;

  extensions: CentrexExtensionModel[] = [];
  targetTypes = [
    { value: 'extension', label: 'Extension' },
    { value: 'user', label: 'Utilisateur' },
    { value: 'group', label: 'Groupe' },
  ];

  filteredExtensions: CentrexExtensionModel[] = [];

  private spinner = inject(NgxSpinnerService);

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      sda: CentrexSdaModel;
      organizationId: string;
      platform: string;
      mode: 'view' | 'edit';
    },
    private fb: FormBuilder,
    private sdaService: CentrexSdasService,
    private extensionsService: CentrexExtensionsService,
    private dialogRef: MatDialogRef<SdasFormComponent>,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.spinner.show('spinner');

    this.objectId = this.data.sda.id;
    this.uidEntreprise = this.data.organizationId;
    this.platform = this.data.platform;

    this.form = this.fb.group({
      number: [
        { value: this.data.sda.number, disabled: true },
        Validators.required,
      ],
      target: [
        { value: this.data.sda.target, disabled: this.data.mode === 'view' },
        Validators.required,
      ],
      targetType: [
        { value: this.data.sda.targetType, disabled: true },
        Validators.required,
      ],
    });

    this.extensionsService
      .getExtensions(this.uidEntreprise, this.platform)
      .pipe(
        finalize(() => {
          this.spinner.hide('spinner');
        }),
      )
      .subscribe((exts) => {
        this.extensions = exts.filter((e) => e.type === 'user');
        this.filteredExtensions = this.extensions;

        this.form.get('target')?.valueChanges.subscribe((value) => {
          const searchValue = value?.toString().toLowerCase() || '';

          this.filteredExtensions = this.extensions.filter(
            (ext) =>
              ext.number?.toLowerCase().includes(searchValue) ||
              ext.name?.toLowerCase().includes(searchValue),
          );
        });
      });
  }

  onSave(): void {
    if (this.form.invalid) return;

    this.spinner.show('spinner');

    const updatedSda: CentrexSdaModel = {
      ...this.data.sda,
      ...this.form.getRawValue(),
    };

    this.sdaService
      .updateSda(this.uidEntreprise, this.platform, this.objectId, updatedSda)
      .pipe(
        finalize(() => {
          this.spinner.hide('spinner');
        }),
      )
      .subscribe({
        next: () => {
          this.toastr.success('SDA mise à jour avec succès.', 'Succès');
          this.dialogRef.close('refresh');
        },
        error: (err) => {
          console.error('Erreur mise à jour SDA', err);

          this.toastr.error(
            "Une erreur s'est produite lors de la mise à jour.",
            'Erreur',
          );
        },
      });
  }
}
